import { Box, Button, CircularProgress, Grid2 } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getDelayCodes } from "../repositories/DelayCodeRepository";
import { DelayCode } from "../models/DelayCode";
import { DelayCodeFilterMode } from "../selectors/DelayCodeTreePicker";

type Props = {
	assetId: number | null;
	selectedCodeId: number | null;
	onSelect: (code: DelayCode) => void;
	disabled?: boolean;
	/**
	 * Filter mode for "Passing the Ball" workflow:
	 * - "all": Show all QuickButtonEnabled codes (default)
	 * - "branches-only": Only show branch codes (for operators)
	 * - "children-of": Only show children of branchCodeId with QuickButtonEnabled (for specialists)
	 */
	filterMode?: DelayCodeFilterMode;
	/**
	 * When filterMode is "children-of", specifies which branch to filter by.
	 */
	branchCodeId?: number | null;
};

export const QuickDelayCodeButtons = ({
	assetId,
	selectedCodeId,
	onSelect,
	disabled = false,
	filterMode = "all",
	branchCodeId,
}: Props) => {
	const { data: codes, isLoading } = useQuery({
		queryKey: ["delayCodes", assetId],
		queryFn: () => getDelayCodes(assetId),
		enabled: assetId !== null,
	});

	const quickCodes = useMemo(() => {
		if (!codes) return [];

		// In operator mode (branches-only), show ALL branch codes as quick buttons
		// regardless of QuickButtonEnabled or IsSelectable - this is the core UX for "Passing the Ball"
		// Branch codes are normally not selectable (you select their children), but in operator mode
		// we WANT to allow selecting them as a quick area classification
		if (filterMode === "branches-only") {
			return codes.filter((code) => code.BranchPermissionAction !== null);
		}

		// Default: filter by QuickButtonEnabled and IsSelectable
		let filtered = codes.filter(
			(code) => code.QuickButtonEnabled && code.IsSelectable,
		);

		// In specialist mode, further filter to descendants of the branch
		if (filterMode === "children-of" && branchCodeId) {
			// Get all descendant IDs recursively
			const getDescendantIds = (parentId: number): number[] => {
				const children = codes.filter((c) => c.ParentDelayCodeId === parentId);
				const childIds = children.map((c) => c.DelayCodeId as number);
				const grandchildIds = childIds.flatMap((id) => getDescendantIds(id));
				return [...childIds, ...grandchildIds];
			};

			const descendantIds = new Set([
				branchCodeId,
				...getDescendantIds(branchCodeId),
			]);

			// Only show QuickButtonEnabled codes that are descendants of the branch
			filtered = filtered.filter((code) =>
				descendantIds.has(code.DelayCodeId as number),
			);
		}

		return filtered;
	}, [codes, filterMode, branchCodeId]);

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" p={2}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	if (quickCodes.length === 0) {
		return null;
	}

	const isSelected = (codeId: number | null) => selectedCodeId === codeId;

	return (
		<Grid2 container spacing={1}>
			{quickCodes.map((code) => (
				<Grid2 key={code.DelayCodeId} size={{ xs: 6, sm: 4 }}>
					<Button
						fullWidth
						variant="contained"
						onClick={() => onSelect(code)}
						disabled={disabled}
						sx={{
							textTransform: "none",
							fontSize: "0.75rem",
							py: 1,
							px: 0.5,
							minHeight: 48,
							lineHeight: 1.2,
							whiteSpace: "normal",
							wordBreak: "break-word",
							// Default: gray background
							backgroundColor: isSelected(code.DelayCodeId)
								? "primary.main"
								: "grey.300",
							color: isSelected(code.DelayCodeId)
								? "primary.contrastText"
								: "text.primary",
							"&:hover": {
								backgroundColor: isSelected(code.DelayCodeId)
									? "primary.dark"
									: "grey.400",
							},
						}}
					>
						{code.DelayCodeName}
					</Button>
				</Grid2>
			))}
		</Grid2>
	);
};
